import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  TextField,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import deemedLogoM from "../navbar/images/deemedLogo2.png";

// Icons
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { WhatsApp, YouTube } from "@mui/icons-material";
import WhatsAppFloat from "../WhatsappButton/WhatsAppFloat";

const FooterWrapper = styled("div")`
  width: 100%;
  background-color: #1e293b;
  color: #f8fafc;
  padding: 20px 0;
  display: flex;
  justify-content: space-around;
`;

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#1e293b",
  color: "#f8fafc",
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(4),
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(3),
  position: "relative",
  paddingBottom: theme.spacing(1),
  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 40,
    height: 2,
    backgroundColor: theme.palette.primary.main,
  },
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: "#cbd5e1",
  display: "block",
  marginBottom: theme.spacing(1.5),
  textDecoration: "none",
  transition: "color 0.2s ease",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: "#f8fafc",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  marginRight: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    transform: "translateY(-3px)",
  },
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(2),
  "& svg": {
    marginRight: theme.spacing(1.5),
    color: theme.palette.primary.main,
  },
}));

const NewsletterBox = styled(Box)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.05)",
  padding: theme.spacing(3),
  borderRadius: 12,
  marginTop: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    borderRadius: theme.shape.borderRadius,
    color: "#f8fafc",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.25)",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#cbd5e1",
  },
}));

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const currentYear = new Date().getFullYear();

  return (
    <FooterWrapper>
      <FooterContainer>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Company Info & Logo */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <img
                  alt="Deemed Logo"
                  src={deemedLogoM}
                  className="desktop-logo"
                />
              </Box>

              

              <Typography variant="body2" color="#cbd5e1" sx={{ mb: 3 }}>
                We bring health to your fingertips, making it easier for
                everyone to access better health care services from anywhere,
                anytime.
              </Typography>
             

              <Box sx={{ mt: 2 }}>
                <a
                  href="https://web.facebook.com/Deedmedonline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon size="small" aria-label="facebook">
                    <FacebookIcon fontSize="small" />
                  </SocialIcon>
                </a>
                <a
                  href="https://www.facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon size="small" aria-label="twitter">
                    <TwitterIcon fontSize="small" />
                  </SocialIcon>
                </a>
                <a
                  href="https://www.instagram.com/deedmed/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon size="small" aria-label="instagram">
                    <InstagramIcon fontSize="small" />
                  </SocialIcon>
                </a>
                <a
                  href="https://www.linkedin.com/company/deedmed-lives-pvt-ltd"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon size="small" aria-label="linkedin">
                    <LinkedInIcon fontSize="small" />
                  </SocialIcon>
                </a>
                <a
                  href="https://www.youtube.com/@deedmedlives"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon size="small" aria-label="linkedin">
                    <YouTube fontSize="small" />
                  </SocialIcon>
                </a>
              </Box>
             
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} sm={6} md={2}>
              <FooterTitle variant="subtitle1" color="white">
                For Patients
              </FooterTitle>
              <FooterLink href="/doctors">Find Health Professionals</FooterLink>
              <FooterLink
                href="https://news.deedmed.lk"
                target="_blank"
                rel="noopener"
              >
                Health Blog
              </FooterLink>
              {/* <FooterLink href="/book-appointment">Book Appointment</FooterLink>
            <FooterLink href="/health-blog">Health Blog</FooterLink>
            <FooterLink href="/patient-dashboard">Patient Dashboard</FooterLink>
            <FooterLink href="/medical-records">Medical Records</FooterLink> */}
            </Grid>

            {/* Services */}
            <Grid item xs={6} sm={6} md={2}>
              <FooterTitle variant="subtitle1" color="white">
                For Health Professionals
              </FooterTitle>
              <FooterLink
                style={{ cursor: "pointer" }}
                onClick={() => {
                  const doctorUrl =
                    process.env.REACT_APP_DOCTOR_PORTAL_URL + "registration"; // CRA
                  // const doctorUrl = import.meta.env.VITE_DOCTOR_URL; // Vite
                  // console.log("Doctor URL:", doctorUrl);
                  if (doctorUrl) {
                    window.open(doctorUrl, "_blank", "noopener,noreferrer");
                  } else {
                    console.error("Doctor URL is not set");
                  }
                }}
              >
                Join as Health Professional
              </FooterLink>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} sm={6} md={3}>
              <FooterTitle variant="subtitle1" color="white">
                Contact Us
              </FooterTitle>
              <ContactItem>
                <LocationOnIcon />
                <Typography variant="body2" color="#cbd5e1">
                  2nd Floor, Paramount Tower, 192/10, 9th Lane, Nawala Road,
                  Nawala Sri Lanka
                </Typography>
              </ContactItem>
              {/* <ContactItem>
                <LocalPhoneIcon />
                <Typography variant="body2" color="#cbd5e1">
                  <a
                    style={{ textDecoration: "none", color: "#cbd5e1" }}
                    href="tel:0117109662"
                  >
                    0117 109 662
                  </a>
                </Typography>
              </ContactItem> */}
              <ContactItem>
                <LocalPhoneIcon />
                <Typography variant="body2" color="#cbd5e1">
                  <a
                    style={{ textDecoration: "none", color: "#cbd5e1" }}
                    href="tel:0707665522"
                  >
                    070 7 665522
                  </a>
                </Typography>
              </ContactItem>
              <ContactItem>
                <WhatsApp />
                <Typography variant="body2" color="#cbd5e1">
                  <a
                    style={{ textDecoration: "none", color: "#cbd5e1" }}
                    href="https://wa.me/94707665522?text=Can%20I%20know%20more%20details?%20"
                  >
                    070 7 665522
                  </a>
                </Typography>
              </ContactItem>
              <ContactItem>
                <EmailIcon />
                <Typography variant="body2" color="#cbd5e1">
                  <a
                    style={{ textDecoration: "none", color: "#cbd5e1" }}
                    href="mailto:info@deedmed.lk"
                  >
                    info@deedmed.lk
                  </a>
                </Typography>
              </ContactItem>
            </Grid>

<WhatsAppFloat />
            {/* Newsletter */}
            {/* <Grid item xs={12} sm={6} md={3}>
            <FooterTitle variant="subtitle1" color="white">
              Newsletter
            </FooterTitle>
            <Typography variant="body2" color="#cbd5e1" sx={{ mb: 2 }}>
              Subscribe to our newsletter for the latest health tips and
              updates.
            </Typography>
          </Grid> */}
          </Grid>

          <Divider
            sx={{ my: 4, backgroundColor: "rgba(255, 255, 255, 0.1)" }}
          />

          {/* Bottom Footer */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md={8}>
               <Typography variant="body2" color="#94a3b8" sx={{ mb: 2, fontSize:'11px', textAlign:"left" }}>
                v.{process.env.REACT_APP_RELEASE_VERSION}
              </Typography>
              <Typography variant="body2" color="#94a3b8">
                © {currentYear} Deedmed Lives (Pvt). Ltd. All rights reserved.
              </Typography>
               
            </Grid>

            <Grid item xs={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                }}
              >
                <Link
                  href="/term-conditions"
                  sx={{
                    color: "#94a3b8",
                    mr: 2,
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                  target="_blank"
                >
                  Terms of Service
                </Link>
                <Link
                  href="/faqs"
                  sx={{
                    color: "#94a3b8",
                    mr: 2,
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                  target="_blank"
                >
                  Frequently Asked Questions
                </Link>
                <Link
                  href="/privacy-policy"
                  sx={{
                    color: "#94a3b8",
                    mr: 2,
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                  target="_blank"
                >
                  Privacy Policy
                </Link>
                {/* <Link
                href="/cookies"
                sx={{
                  color: "#94a3b8",
                  textDecoration: "none",
                  fontSize: "0.875rem",
                }}
              >
                Cookie Policy
              </Link> */}
              </Box>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={2}
            marginTop={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} md={12}>
              <Typography
                variant="body2"
                color="#94a3b8"
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                Developed by
                <img
                  height={18}
                  style={{ margin: "0px 10px 2px 10px" }}
                  alt="Deemed Logo"
                  src={deemedLogoM}
                  className="desktop-logo"
                />
                Lives
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default Footer;
